import { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ShieldCheck } from 'lucide-react';
import { MaskedLine, EASE } from '@/components/Reveal';
import { LangLink } from '@/i18n/LangContext';

export const Hero = ({ hero }) => {
  const [imgSrc, setImgSrc] = useState(hero.image || '/assets/home-hero-consulenza.webp');

  return (
    <section data-testid="home-hero" className="relative overflow-hidden bg-background">
      <div className="mx-auto max-w-site px-5 md:px-8 grid lg:grid-cols-12 gap-10 lg:gap-8 items-center min-h-[88vh] py-16 lg:py-0">
        <div className="lg:col-span-7 relative z-10">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15, ease: EASE }}
            className="text-xs uppercase tracking-[0.25em] font-semibold text-primary mb-6 flex items-center gap-2"
            data-testid="hero-eyebrow"
          >
            <ShieldCheck className="h-4 w-4" />
            {hero.eyebrow}
          </motion.p>

          <h1 className="font-heading font-bold tracking-tighter leading-[0.98] text-5xl sm:text-6xl lg:text-7xl xl:text-[5.25rem] text-foreground" data-testid="hero-title">
            {hero.lines.map((line, i) => (
              <MaskedLine key={i} delay={0.25 + i * 0.12} className={i >= hero.accentFrom ? 'text-primary' : ''}>
                {line}
              </MaskedLine>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85, ease: EASE }}
            className="mt-7 text-base md:text-lg leading-relaxed text-foreground/70 max-w-xl"
            data-testid="hero-subtitle"
          >
            {hero.subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1.0, ease: EASE }}
            className="mt-9 flex flex-wrap gap-4"
          >
            <LangLink
              to="/soluzioni"
              data-testid="hero-cta-soluzioni"
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-7 py-4 text-sm font-semibold rounded-lg hover:bg-brand-dark transition-colors duration-200 group"
            >
              {hero.ctaPrimary}
              <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
            </LangLink>
            <LangLink
              to="/prenota"
              data-testid="hero-cta-consulente"
              className="inline-flex items-center gap-2 border border-foreground/20 px-7 py-4 text-sm font-semibold rounded-lg hover:border-primary hover:text-primary transition-colors duration-200"
            >
              {hero.ctaSecondary}
            </LangLink>
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative block mt-3 lg:mt-0">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.5, ease: EASE }}
            className="relative"
          >
            <div className="absolute -top-8 -left-8 w-40 h-40 border border-primary/20 bg-transparent z-0" aria-hidden="true" />
            <div className="relative overflow-hidden aspect-[16/10] lg:aspect-[4/5] z-10 rounded-xl bg-brand-pale shadow-[0_40px_80px_-40px_rgba(10,61,32,0.35)]">
              <motion.img
                src={imgSrc}
                alt={hero.imageAlt || 'Consulente assicurativo durante un incontro con clienti'}
                fetchpriority="high"
                loading="eager"
                decoding="async"
                style={{ transform: 'none', objectPosition: 'center bottom' }}
                className="w-full h-[108%] object-cover"
                data-testid="hero-image"
                onError={() => setImgSrc('/assets/home-hero.webp')}
              />
            </div>
            <div className="absolute -bottom-8 -right-8 w-40 h-40 border border-primary/20 bg-transparent z-0" aria-hidden="true" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};
